import React, {useEffect, useRef} from 'react'
import {useIntl} from 'react-intl'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {useDispatch, useSelector} from 'react-redux'
import Modal from 'react-bootstrap/Modal'
import {Datepicker} from '../../../components'
import {contractAddendumCreate, contractAddendumUpdate} from '../../../redux/slices/project.slice'

const selectLoading = (state) => state.project.loading

function ContractAddendumModal({show, onHide, contractId, editItem}) {
  const intl = useIntl()
  const dispatch = useDispatch()
  const loading = useSelector(selectLoading)
  const isEditing = !!editItem

  const formikRef = useRef()
  const isFormPopulatedRef = useRef(false)

  const validSchema = Yup.object().shape({
    ContractId: Yup.number().required(),
  })

  const initialValues = {
    ContractId: contractId,
    AddendumDate: null,
    AddendumDescription: '',
    ExtensionDate: null,
    ExtensionDescription: '',
  }

  const formik = useFormik({
    initialValues,
    validationSchema: validSchema,
    onSubmit: async (values, {setSubmitting, resetForm}) => {
      setSubmitting(true)
      if (isEditing) {
        await dispatch(contractAddendumUpdate({Id: editItem.id, ...values}))
      } else {
        await dispatch(contractAddendumCreate(values))
      }
      resetForm()
      onHide(true)
    },
  })

  formikRef.current = formik

  useEffect(() => {
    if (show) {
      isFormPopulatedRef.current = false
    }
  }, [show])

  useEffect(() => {
    if (editItem && formikRef.current && !isFormPopulatedRef.current) {
      formikRef.current.setFieldValue('ContractId', contractId)
      formikRef.current.setFieldValue('AddendumDate', editItem.addendumDate ? new Date(editItem.addendumDate) : null)
      formikRef.current.setFieldValue('AddendumDescription', editItem.addendumDescription || '')
      formikRef.current.setFieldValue('ExtensionDate', editItem.extensionDate ? new Date(editItem.extensionDate) : null)
      formikRef.current.setFieldValue('ExtensionDescription', editItem.extensionDescription || '')
      isFormPopulatedRef.current = true
    }
    if (!editItem && !isFormPopulatedRef.current) {
      formikRef.current.resetForm()
      formikRef.current.setFieldValue('ContractId', contractId)
      isFormPopulatedRef.current = true
    }
  }, [editItem, contractId])

  return (
    <Modal show={show} onHide={() => onHide(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {isEditing
            ? intl.formatMessage({id: 'CONTRACT.ADDENDUM.EDIT'})
            : intl.formatMessage({id: 'CONTRACT.ADDENDUM.ADD'})}
        </Modal.Title>
      </Modal.Header>
      <form onSubmit={formik.handleSubmit} noValidate>
        <Modal.Body>
          <div className='row mb-5'>
            <label className='col-lg-4 fw-bold fs-6 mb-2'>
              {intl.formatMessage({id: 'CONTRACT.ADDENDUM.ADDENDUMDATE'})}
            </label>
            <div className='col-lg-8 fv-row'>
              <Datepicker
                {...formik.getFieldProps('AddendumDate')}
                onChange={([date]) => formik.setFieldValue('AddendumDate', date)}
              />
            </div>
          </div>

          <div className='row mb-5'>
            <label className='col-lg-4 fw-bold fs-6 mb-2'>
              {intl.formatMessage({id: 'CONTRACT.ADDENDUM.ADDENDUMDESCRIPTION'})}
            </label>
            <div className='col-lg-8 fv-row'>
              <textarea
                className='form-control form-control-solid'
                rows={3}
                {...formik.getFieldProps('AddendumDescription')}
              />
            </div>
          </div>

          <div className='row mb-5'>
            <label className='col-lg-4 fw-bold fs-6 mb-2'>
              {intl.formatMessage({id: 'CONTRACT.ADDENDUM.EXTENSIONDATE'})}
            </label>
            <div className='col-lg-8 fv-row'>
              <Datepicker
                {...formik.getFieldProps('ExtensionDate')}
                onChange={([date]) => formik.setFieldValue('ExtensionDate', date)}
              />
            </div>
          </div>

          <div className='row mb-5'>
            <label className='col-lg-4 fw-bold fs-6 mb-2'>
              {intl.formatMessage({id: 'CONTRACT.ADDENDUM.EXTENSIONDESCRIPTION'})}
            </label>
            <div className='col-lg-8 fv-row'>
              <textarea
                className='form-control form-control-solid'
                rows={3}
                {...formik.getFieldProps('ExtensionDescription')}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type='button' className='btn btn-light' onClick={() => onHide(false)}>
            {intl.formatMessage({id: 'BUTTON.CANCEL'})}
          </button>
          <button type='submit' className='btn btn-primary' disabled={formik.isSubmitting || loading}>
            {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

export {ContractAddendumModal}
